import type { CheckRunExpandableRowProps, PaymentFile, PaymentFileStatus } from '../types'
import type { ColumnsType } from 'antd/es/table'
import type { RenderExpandIcon } from 'rc-table/lib/interface'

import {
  DeleteOutlined,
  EllipsisOutlined,
  FlagFilled,
  PauseCircleFilled,
  RollbackOutlined
} from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Dropdown, Modal, notification, Skeleton, Table, Tag, Tooltip } from 'antd'
import cx from 'classnames'
import { format, parseISO } from 'date-fns'
import { useCallback, useContext, useMemo, useReducer, useState } from 'react'

import $api from '../../../../API'
import { Role } from '../../../../domain/role.enum'
import { useAppSelector } from '../../../../store'
import {
  baseCellClassName,
  baseHeaderCellClassName,
  BatchSelectionContext,
  checkRunExpandableRowInitialState,
  statusMapper
} from '../constants'
import { currencyFormatter, pluralize } from '../functions'
import { checkRunExpandableRowReducer } from '../store'

import { CheckRunGeneralInformationWrapper } from './check-run-general-information-wrapper'
import { ExpandIcon } from './expand-icon'
import { PaymentFileExpandableRow } from './payment-file-expandable-row'
import { Duplicate } from '../../../../components/Icons/Duplicate/Duplicate'

type QueryKeyWithParams<T> = T extends Array<string | infer U> ? [string, U] : never

export const CheckRunExpandableRow = (props: CheckRunExpandableRowProps): JSX.Element => {
  const [state, dispatch] = useReducer(
    checkRunExpandableRowReducer,
    checkRunExpandableRowInitialState
  )

  const [processingFileId, setProcessingFileId] = useState<string | null>(null)

  const user = useAppSelector(state => state.app.user)
  const queryClient = useQueryClient()

  const has = useCallback(
    (roles: Role[]): boolean => {
      return roles.some(role => user.roles.includes(role))
    },
    [user.roles]
  )

  const query = useQuery({
    queryKey: ['check-run-payment-files', { checkRunId: props.id, groupId: props.group.id }],

    async queryFn({ queryKey, signal }) {
      const [, { checkRunId, groupId }] = queryKey as QueryKeyWithParams<typeof queryKey>

      const response = await $api.get<PaymentFile[]>(
        `/check-runs/${checkRunId}/groups/${groupId}`,
        {
          signal
        }
      )

      return response.data
    }
  })

  const deletePaymentFileMutation = useMutation({
    async mutationFn({ paymentFileId }: { paymentFileId: string }) {
      await $api.delete(`/check-runs/${props.id}/payment-files/${paymentFileId}`)
    },
    onSuccess() {
      notification.success({
        message: 'Payment file deleted',
        description: 'The payment file has been successfully deleted'
      })
      void query.refetch()
      void queryClient.invalidateQueries({ queryKey: ['check-runs', props.id] })
      void queryClient.invalidateQueries({ queryKey: ['release-payment-files'] })
      void queryClient.invalidateQueries({ queryKey: ['check-runs', 'meta', 'totals'] })
      void queryClient.invalidateQueries({ queryKey: ['check-run-totals'] })
    },
    onError(error) {
      const err = error as Error & {
        response?: { data?: { message?: string; description?: string } }
      }
      notification.error({
        message: err.response?.data?.message ?? 'Failed to delete payment file',
        description: err.response?.data?.description
      })
    }
  })

  const restorePaymentFileMutation = useMutation({
    async mutationFn({ paymentFileId }: { paymentFileId: string }) {
      await $api.post(`/check-runs/${props.id}/payment-files/${paymentFileId}/restore`)
    },
    onSuccess() {
      notification.success({
        message: 'Payment file restored',
        description: 'The payment file has been successfully restored'
      })
      void query.refetch()
      void queryClient.invalidateQueries({ queryKey: ['check-runs', props.id] })
      void queryClient.invalidateQueries({ queryKey: ['release-payment-files'] })
      void queryClient.invalidateQueries({ queryKey: ['check-runs', 'meta', 'totals'] })
      void queryClient.invalidateQueries({ queryKey: ['check-run-totals'] })
    },
    onError(error) {
      const err = error as Error & {
        response?: { data?: { message?: string; description?: string } }
      }
      notification.error({
        message: err.response?.data?.message ?? 'Failed to restore payment file',
        description: err.response?.data?.description
      })
    }
  })

  const handleDeleteClick = useCallback(
    (record: PaymentFile) => {
      Modal.confirm({
        title: 'Delete Payment File',
        content: (
          <p>
            Are you sure you want to delete the payment file <strong>{record.filename}</strong>?
          </p>
        ),
        icon: <DeleteOutlined className='text-red-500' />,
        okText: 'Delete',
        okButtonProps: { danger: true },
        onOk() {
          setProcessingFileId(record.id)
          return deletePaymentFileMutation
            .mutateAsync({ paymentFileId: record.id })
            .finally(() => setProcessingFileId(null))
        }
      })
    },
    [deletePaymentFileMutation]
  )

  const handleRestoreClick = useCallback(
    (record: PaymentFile) => {
      Modal.confirm({
        title: 'Restore Payment File',
        content: (
          <p>
            Are you sure you want to restore the payment file <strong>{record.filename}</strong>?
          </p>
        ),
        icon: <RollbackOutlined className='text-green-500' />,
        okText: 'Restore',
        onOk() {
          setProcessingFileId(record.id)
          return restorePaymentFileMutation
            .mutateAsync({ paymentFileId: record.id })
            .finally(() => setProcessingFileId(null))
        }
      })
    },
    [restorePaymentFileMutation]
  )

  const paymentFiles = query.data ?? []

  const isSameStatus =
    paymentFiles.length > 0 &&
    paymentFiles.every(file => file.status.id === paymentFiles[0]?.status.id)

  const columns = useMemo<ColumnsType<PaymentFile>>(
    () => [
      {
        title: 'File Name',
        dataIndex: 'filename',
        render(value: string, record: PaymentFile) {
          return (
            <span className={cx({ 'line-through text-gray-400': record.deleted_at })}>{value}</span>
          )
        },
        onCell(record: PaymentFile) {
          return {
            className: cx(baseCellClassName, { 'bg-gray-50': record.deleted_at })
          }
        },
        onHeaderCell() {
          return {
            className: cx(
              'text-[0.675rem] uppercase font-lato !bg-transparent',
              baseHeaderCellClassName
            )
          }
        }
      },
      {
        title: 'File Date',
        dataIndex: 'date',
        align: 'center',
        render(value: string) {
          return format(parseISO(value), 'MM/dd/yyyy')
        },
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: 'text-[0.675rem] uppercase font-lato !bg-transparent'
          }
        }
      },
      {
        title: 'LuminX Total',
        align: 'right',
        render(_, record) {
          const amount = record.file_total
          return currencyFormatter.format(amount)
        },
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: 'text-[0.675rem] uppercase font-lato !bg-transparent'
          }
        }
      },
      {
        title: 'Zelis Total',
        align: 'right',
        render(_, record) {
          const amount = record.proc_total
          return currencyFormatter.format(amount)
        },
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: 'text-[0.675rem] uppercase font-lato !bg-transparent'
          }
        }
      },
      {
        title: 'Amount',
        dataIndex: ['eobs_summary', 'check_amount_sum'],
        align: 'right',
        render(value) {
          return currencyFormatter.format(value)
        },
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: 'text-[0.675rem] uppercase font-lato !bg-transparent'
          }
        }
      },
      ...(isSameStatus
        ? []
        : ([
            {
              title: 'Status',
              dataIndex: 'status',
              align: 'center',
              render(value: PaymentFileStatus, record: PaymentFile) {
                if (record.deleted_at) {
                  return (
                    <Tooltip
                      title={`Deleted at ${format(parseISO(record.deleted_at), 'MM/dd/yyyy hh:mm a')}`}
                    >
                      <Tag color='orange' className='font-lato size-xs'>
                        Deleted
                      </Tag>
                    </Tooltip>
                  )
                }

                const config = statusMapper[value.id]

                return (
                  <Tag color={config?.color ?? 'default'} className='font-lato size-xs'>
                    {config?.name ?? value.name}
                  </Tag>
                )
              },
              onCell() {
                return {
                  className: cx(baseCellClassName)
                }
              },
              onHeaderCell() {
                return {
                  className: 'text-[0.675rem] uppercase font-lato !bg-transparent'
                }
              }
            }
          ] as ColumnsType<PaymentFile>)),
      {
        title: 'Flags',
        key: 'flags',
        align: 'center',
        render(_, record) {
          const onHold = record.eobs_summary.is_on_hold_total
          const allOnHold = record.eobs_summary.is_on_hold_total === record.eobs_summary.total
          const stopLoss = record.eobs_summary.is_flagged_stop_loss_total

          const allStopLoss =
            record.eobs_summary.is_flagged_stop_loss_total === record.eobs_summary.total

          const totalEOBs = record.eobs_summary.total

          return (
            <div className='flex gap-2 justify-center items-center'>
              {onHold > 0 && (
                <Tooltip
                  title={
                    allOnHold
                      ? `This payment file is flagged on hold. ${pluralize(totalEOBs, 'Its single EOB is', `All of its ${totalEOBs} EOBs are`)} flagged on hold.`
                      : `This payment file is partially flagged on hold. ${onHold} ${pluralize(onHold, 'EOB', 'EOBs')} out of ${totalEOBs} ${pluralize(totalEOBs, 'EOB', 'EOBs')} ${pluralize(onHold, 'is', 'are')} flagged on hold.`
                  }
                >
                  <PauseCircleFilled className='text-sm text-yellow-500' />
                </Tooltip>
              )}

              {stopLoss > 0 && (
                <Tooltip
                  title={
                    allStopLoss
                      ? `This payment file is flagged as stop loss. ${pluralize(totalEOBs, 'Its single EOB is', `All of its ${totalEOBs} EOBs are`)} flagged as stop loss.`
                      : `This payment file is partially flagged as stop loss. ${stopLoss} ${pluralize(stopLoss, 'EOB', 'EOBs')} out of ${totalEOBs} ${pluralize(totalEOBs, 'EOB', 'EOBs')} ${pluralize(stopLoss, 'is', 'are')} flagged as stop loss.`
                  }
                >
                  <FlagFilled className='text-sm text-lucent-red' />
                </Tooltip>
              )}

              {record.eobs_summary.has_multiple_patients_total > 0 && (
                <Tooltip
                  title={`${record.eobs_summary.has_multiple_patients_total} EOBs have multiple patients per claim`}
                >
                  <Duplicate />
                </Tooltip>
              )}
            </div>
          )
        },
        onCell() {
          return {
            className: cx(baseCellClassName)
          }
        },
        onHeaderCell() {
          return {
            className: cx(baseHeaderCellClassName)
          }
        }
      },
      ...(has([Role.Administrator])
        ? [
            {
              key: 'actions',
              align: 'center' as const,
              width: 40,
              render(_: unknown, record: PaymentFile) {
                const isDeleted = record.deleted_at !== null

                // Cannot delete completed (5) or voided (8) files
                const canDelete = !isDeleted && !['5', '8'].includes(record.status.id)
                const isProcessing = processingFileId === record.id

                const items = [
                  ...(isDeleted
                    ? [
                        {
                          key: 'restore',
                          label: 'Restore',
                          icon: <RollbackOutlined />
                        }
                      ]
                    : []),
                  ...(canDelete
                    ? [
                        {
                          key: 'delete',
                          label: 'Delete',
                          icon: <DeleteOutlined />,
                          danger: true
                        }
                      ]
                    : [])
                ]

                if (items.length === 0) return null

                return (
                  <div onClick={e => e.stopPropagation()}>
                    <Dropdown
                      menu={{
                        items,
                        onClick(info) {
                          info.domEvent.preventDefault()
                          info.domEvent.stopPropagation()

                          switch (info.key) {
                            case 'delete': {
                              handleDeleteClick(record)
                              break
                            }
                            case 'restore': {
                              handleRestoreClick(record)
                              break
                            }
                          }
                        }
                      }}
                    >
                      <Button
                        className='flex justify-center items-center'
                        icon={<EllipsisOutlined />}
                        type='text'
                        shape='circle'
                        size='small'
                        loading={isProcessing}
                      />
                    </Dropdown>
                  </div>
                )
              },
              onCell() {
                return {
                  className: cx(baseCellClassName)
                }
              },
              onHeaderCell() {
                return {
                  className: cx(baseHeaderCellClassName)
                }
              }
            }
          ]
        : [])
    ],
    [isSameStatus, has, handleDeleteClick, handleRestoreClick, processingFileId]
  )

  const batchSelectionContext = useContext(BatchSelectionContext)

  return (
    <div className='relative !p-0'>
      <div className='absolute top-0 left-0 z-10 w-1 h-full bg-blue-200' />

      <CheckRunGeneralInformationWrapper checkRunId={props.id} groupId={props.group.id} />

      <Table
        size='small'
        rowKey='id'
        className='rounded-none payment-files-table [&_.ant-table]:![margin-inline:_0]'
        rowClassName={(record: PaymentFile) => cx({ 'bg-gray-50 opacity-60': record.deleted_at })}
        rowSelection={{
          columnWidth: 3.5 * 16,
          hideSelectAll: true,
          selectedRowKeys: batchSelectionContext.selectedPaymentFileIds,

          getCheckboxProps() {
            return {
              disabled: true,
              className: 'hidden'
            }
          }
        }}
        columns={
          !query.isLoading
            ? columns
            : columns.map(column => {
                return {
                  ...column,
                  render: function renderPlaceholder() {
                    return <Skeleton key={column.key} title active={true} paragraph={false} />
                  }
                }
              })
        }
        dataSource={
          !query.isLoading
            ? paymentFiles
            : ([...Array(2)].map((_, index) => ({
                id: `key${index}`
              })) as unknown as PaymentFile[])
        }
        pagination={false}
        expandable={{
          expandedRowKeys: state.expandedRowIds,
          onExpandedRowsChange(expandedRowKeys) {
            dispatch({
              type: 'rows/expand',
              payload: expandedRowKeys.map(key => key.toString(10))
            })
          },
          expandIcon: ExpandIcon as RenderExpandIcon<PaymentFile>,
          showExpandColumn: false,
          expandRowByClick: true,
          expandedRowRender(record, _, __, expanded) {
            if (!expanded) {
              return null
            }

            return (
              <PaymentFileExpandableRow
                checkRunId={props.id}
                groupId={props.group.id}
                paymentFileId={record.id}
                paymentFileName={record.filename}
              />
            )
          }
        }}
      />
    </div>
  )
}
